import { ThemeEvents } from '@theme/events';

class AirCinemaProjectorMateCrossSell extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @type {HTMLElement | null} */
  #formComponent = null;

  /** @type {HTMLButtonElement | null} */
  #button = null;

  /** @type {HTMLElement | null} */
  #status = null;

  /** @type {number | undefined} */
  #timeout;

  #processing = false;

  connectedCallback() {
    if (this.#abortController) return;

    this.#abortController = new AbortController();
    this.#form = this.querySelector('form');
    this.#formComponent = this.querySelector('product-form-component');
    this.#button = this.querySelector('[ref="addToCartButton"]');
    this.#status = this.querySelector('[data-cross-sell-status]');

    if (!this.#form || !this.#formComponent || !this.#button) return;

    const { signal } = this.#abortController;
    this.#form.addEventListener('submit', this.#handleSubmit, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartError, { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = undefined;
    this.#clearTimeout();
  }

  #handleSubmit = (event) => {
    if (this.#processing) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    this.#processing = true;
    if (this.#status) {
      this.#status.textContent = '';
      delete this.#status.dataset.state;
    }

    queueMicrotask(() => {
      this.#setProcessingState(true);
    });

    this.#clearTimeout();
    this.#timeout = window.setTimeout(() => {
      const message = this.dataset.timeoutMessage || 'Unable to add the stand. Please try again.';
      this.#finishProcessing(message, 'error');
    }, 15000);
  };

  #handleCartUpdate = (event) => {
    if (!this.#processing || !this.#matchesEvent(event)) return;
    const message = this.dataset.successMessage || 'Item added to your cart.';
    this.#finishProcessing(message, 'success');
  };

  #handleCartError = (event) => {
    if (!this.#processing || !this.#matchesEvent(event)) return;
    const message = this.dataset.errorMessage || 'Unable to add this item. Please try again.';
    this.#finishProcessing(message, 'error');
  };

  #matchesEvent(event) {
    const sourceId = event.detail?.sourceId?.toString();
    const eventProductId = event.detail?.data?.productId?.toString();
    const componentId = this.#formComponent?.id;
    const formId = this.#form?.id;

    return (
      sourceId === componentId ||
      sourceId === formId ||
      eventProductId === this.dataset.productId
    );
  }

  #setProcessingState(isProcessing) {
    if (this.#button) {
      this.#button.disabled = isProcessing;
      this.#button.toggleAttribute('aria-busy', isProcessing);
    }

    this.#formComponent?.toggleAttribute('aria-busy', isProcessing);
    this.toggleAttribute('data-processing', isProcessing);
  }

  #finishProcessing(message = '', state = '') {
    this.#processing = false;
    this.#clearTimeout();
    this.#setProcessingState(false);

    if (!this.#status) return;

    this.#status.textContent = message;
    if (state) {
      this.#status.dataset.state = state;
    } else {
      delete this.#status.dataset.state;
    }
  }

  #clearTimeout() {
    if (!this.#timeout) return;
    window.clearTimeout(this.#timeout);
    this.#timeout = undefined;
  }
}

if (!customElements.get('aircinema-projectormate-cross-sell')) {
  customElements.define('aircinema-projectormate-cross-sell', AirCinemaProjectorMateCrossSell);
}
